import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'

const api = '/koha/item'
// Keep the original library dropdown choices, independently of discovered data.
export const COLLECTION_BARCODE_CODES = [
    { prefix: 'T', display_name: '教職員論文' },
    { prefix: 'S', display_name: '學生專題報告' },
    { prefix: 'CP', display_name: '中文期刊' },
    { prefix: 'EP', display_name: '電子期刊' },
    { prefix: 'SM', display_name: '西文書' },
    { prefix: '__NONE__', display_name: '中文圖書' },
]
export const barcodeCodeLabel = code => `${code.prefix === '__NONE__' ? '（無代碼）' : code.prefix} － ${code.display_name || '未設定名稱'}`
export const COLLECTION_BARCODE_PREFIX_OPTIONS = COLLECTION_BARCODE_CODES.map(code => ({ value: code.prefix, label: barcodeCodeLabel(code) }))
export const formatBarcodeCount = value => value === null || value === undefined ? '-' : String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

export function validateBarcodeRange(input) {
    if (!input.prefix) throw new Error('請選擇館藏條碼代碼')
    const start = input.start_no.trim()
    const end = input.end_no.trim()
    if ([start, end].some(value => value !== '' && !/^[0-9]{1,65}$/.test(value))) throw new Error('條碼起訖號須為最多 65 位的非負整數')
    const a = start.replace(/^0+/, '') || '0'
    const b = end.replace(/^0+/, '') || '0'
    if (start !== '' && end !== '' && (a.length > b.length || (a.length === b.length && a > b))) throw new Error('條碼起號不可大於迄號')
    return { prefix: input.prefix, start_no: start, end_no: end, cur_page: 1, size: 50 }
}

const paramsFor = query => ({ item_barcode_prefix: query.prefix, item_barcode_start: query.start_no || undefined,
    item_barcode_end: query.end_no || undefined, cur_page: query.cur_page, size: query.size })
const errorMessage = error => error.response?.data?.message || error.message || '館藏條碼資料讀取失敗'

export function useCollectionBarcodeCodes() {
    const [state, setState] = useState({ data: null, loading: true, error: '' })
    const [version, setVersion] = useState(0)
    const reload = useCallback(() => setVersion(value => value + 1), [])
    useEffect(() => {
        const controller = new AbortController()
        let current = true
        setState({ data: null, loading: true, error: '' })
        axios.get(`${api}/barcode_codes`, { signal: controller.signal }).then(response => {
            const body = response.data
            if (body?.status !== 'success' || !Array.isArray(body.data?.codes)) throw new Error(body?.message || '館藏代碼資料格式錯誤')
            if (current) setState({ data: body.data, loading: false, error: '' })
        }).catch(error => { if (current) setState({ data: null, loading: false, error: errorMessage(error) }) })
        return () => { current = false; controller.abort() }
    }, [version])
    return { ...state, reload }
}

export function useCollectionBarcodeOverview(query) {
    const [state, setState] = useState({ key: '', data: null, loading: false, error: '' })
    const key = JSON.stringify(query)
    useEffect(() => {
        if (!query) return undefined
        const controller = new AbortController()
        let current = true
        setState({ key, data: null, loading: true, error: '' })
        axios.get(`${api}/barcode_overview`, { params: paramsFor(query), signal: controller.signal }).then(response => {
            const body = response.data
            if (body?.status !== 'success' || !body.data?.summary || !body.data?.gaps) throw new Error(body?.message || '館藏條碼資料格式錯誤')
            if (current) setState({ key, data: body.data, loading: false, error: '' })
        }).catch(error => { if (current) setState({ key, data: null, loading: false, error: errorMessage(error) }) })
        return () => { current = false; controller.abort() }
    }, [query, key])
    return state.key === key ? state : { data: null, loading: !!query, error: '' }
}

async function reportBlob(path, params, mime) {
    try {
        const response = await axios.get(path, { params, responseType: 'blob' })
        const contentType = response.headers?.['content-type'] || response.data?.type || ''
        if (!contentType.includes(mime)) {
            const text = await response.data.text()
            let message = '報表產生失敗'
            try { message = JSON.parse(text).message || message } catch (_) { /* Non-JSON error page. */ }
            throw new Error(message)
        }
        return response.data
    } catch (error) {
        if (typeof error.response?.data?.text === 'function') {
            const text = await error.response.data.text()
            let message
            try { message = JSON.parse(text).message } catch (_) { /* Non-JSON error page. */ }
            if (message) throw new Error(message)
        }
        throw new Error(errorMessage(error))
    }
}

export async function exportBarcodeOverview(query) {
    const blob = await reportBlob(`${api}/barcode_overview/export`, paramsFor(query), 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `collection_barcode_${query.prefix}.xlsx`
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export async function printBarcodeReport(query = null) {
    const blob = await reportBlob(query ? `${api}/barcode_overview/print` : `${api}/barcode_codes/print`, query ? paramsFor(query) : undefined, 'text/html')
    const url = URL.createObjectURL(blob)
    const frame = document.createElement('iframe')
    frame.title = query ? '空號報表列印' : '館藏代碼表列印'
    frame.style.cssText = 'position:fixed;width:1px;height:1px;left:-10000px;border:0'
    return new Promise((resolve, reject) => {
        let timer
        const cleanup = () => { clearTimeout(timer); frame.remove(); URL.revokeObjectURL(url) }
        timer = setTimeout(() => { cleanup(); reject(new Error('列印載入逾時，請重試')) }, 120000)
        frame.onload = () => {
            try {
                frame.contentWindow.addEventListener('afterprint', cleanup, { once: true })
                frame.contentWindow.focus()
                frame.contentWindow.print()
                resolve()
            } catch (_) { cleanup(); reject(new Error('無法開啟列印，請重試')) }
        }
        frame.onerror = () => { cleanup(); reject(new Error('列印報表載入失敗，請重試')) }
        frame.src = url
        document.body.appendChild(frame)
    })
}
